// Shared per-team Kokoro TTS voice picker. Used on the Chronicles
// admin page (where operators expect to find it) and inside the
// full Chronicles config editor on Public Board Settings.
// ponytail: single source of truth — both pages render this
// component, both routes save to the same chronicle-config PATCH.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';

// Kokoro voices grouped by accent + gender for the picker.
// Kept in sync with the api's KOKORO_VOICES list.
const KOKORO_VOICE_GROUPS = {
  af: ['af_heart', 'af_alloy', 'af_aoede', 'af_bella', 'af_jessica', 'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky'],
  am: ['am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael', 'am_onyx', 'am_puck', 'am_santa'],
  bf: ['bf_emma', 'bf_isabella', 'bf_alice', 'bf_lily'],
  bm: ['bm_george', 'bm_lewis', 'bm_daniel', 'bm_fable'],
} as const;

interface ChronicleConfigTts {
  tts_voice: string;
}

export function ChronicleTtsVoicePicker({ teamId }: { teamId: string }) {
  const qc = useQueryClient();
  const cfg = useQuery<ChronicleConfigTts>({
    queryKey: ['chronicle-config', teamId],
    queryFn: () => api<ChronicleConfigTts>(`/api/admin/teams/${teamId}/chronicle-config`),
    enabled: !!teamId,
  });
  const [voice, setVoice] = useState('af_heart');
  const [seeded, setSeeded] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  // Seed from server once per team; the seedRef guard keeps the
  // user's unsaved edits if the query refetches (e.g. another
  // admin saved).
  useEffect(() => {
    if (!cfg.data || seeded) return;
    setVoice(cfg.data.tts_voice ?? 'af_heart');
    setSeeded(true);
  }, [cfg.data, seeded]);
  useEffect(() => { setSeeded(false); }, [teamId]);

  const save = useMutation({
    mutationFn: () => api<ChronicleConfigTts>(`/api/admin/teams/${teamId}/chronicle-config`, {
      method: 'PATCH',
      body: { chronicle_tts_voice: voice },
    }),
    onSuccess: (data) => {
      setVoice(data.tts_voice ?? voice);
      qc.invalidateQueries({ queryKey: ['chronicle-config', teamId] });
      setFeedback({ kind: 'ok', msg: 'Saved.' });
    },
    onError: (e) => {
      setFeedback({ kind: 'err', msg: e instanceof ApiError ? e.message : 'Save failed.' });
    },
  });

  return (
    <div className="card space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <label className="label" htmlFor="chronicle-tts-voice">
            Read-aloud voice (Kokoro)
          </label>
          <select
            id="chronicle-tts-voice"
            className="input max-w-xs"
            value={voice}
            onChange={(e) => { setVoice(e.target.value); setFeedback(null); }}
          >
            <optgroup label="American — female">
              {KOKORO_VOICE_GROUPS.af.map((v) => <option key={v} value={v}>{v}</option>)}
            </optgroup>
            <optgroup label="American — male">
              {KOKORO_VOICE_GROUPS.am.map((v) => <option key={v} value={v}>{v}</option>)}
            </optgroup>
            <optgroup label="British — female">
              {KOKORO_VOICE_GROUPS.bf.map((v) => <option key={v} value={v}>{v}</option>)}
            </optgroup>
            <optgroup label="British — male">
              {KOKORO_VOICE_GROUPS.bm.map((v) => <option key={v} value={v}>{v}</option>)}
            </optgroup>
          </select>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setFeedback(null); save.mutate(); }}
          disabled={save.isPending || voice === cfg.data?.tts_voice}
        >
          {save.isPending ? 'Saving…' : 'Save voice'}
        </button>
      </div>
      <p className="text-xs text-ink-dim">
        The wall-TV player uses this voice when reading a paragraph aloud. The TTS cache is keyed on chapter + text + voice, so changing the voice re-synthesizes the next paragraph (a few seconds) and the new voice is heard immediately.
      </p>
      {feedback ? (
        <p
          className={`text-sm ${feedback.kind === 'ok' ? 'text-accent-success' : 'text-accent-danger'}`}
          role="status"
        >
          {feedback.msg}
        </p>
      ) : null}
    </div>
  );
}